<template>
  <div class="form-container" :class="{ isMobile: isMobile }">
    <div class="auth-form">
      <h1 class="auth-title mb-6">Utwórz konto</h1>
      <v-form name="register" @submit.prevent>
        <validation-observer ref="observer" v-slot="{}">
          <ValidationProvider
            v-slot="{ errors }"
            rules="required|email"
            name="e-mail"
          >
            <v-text-field
              v-model="email"
              label="Adres e-mail"
              :error-messages="errors"
              required
              outlined
              persistent-placeholder
              placeholder="Wpisz adres e-mail"
              autocomplete="off"
              name="email"
              class="mb-1"
            />
          </ValidationProvider>

          <ValidationProvider
            v-slot="{ errors }"
            :rules="nameRules"
            name="Imię"
          >
            <v-text-field
              title="Uzupełnij to pole"
              v-model="firstName"
              label="Imię"
              :error-messages="errors"
              outlined
              persistent-placeholder
              placeholder="Wpisz imię"
              autocomplete="off"
              name="firstName"
              class="mb-1"
            />
          </ValidationProvider>

          <ValidationProvider
            v-slot="{ errors }"
            :rules="nameRules"
            name="Nazwisko"
          >
            <v-text-field
              title="Uzupełnij to pole"
              v-model="lastName"
              label="Nazwisko"
              :error-messages="errors"
              outlined
              persistent-placeholder
              placeholder="Wpisz nazwisko"
              autocomplete="off"
              name="lastName"
              class="mb-1"
            />
          </ValidationProvider>

          <DictionaryEntrySelector
            dictionary-name="usr_type"
            :excludeEntries="['usr_type-ofiz', 'usr_type-opraw']"
            rules="required"
            v-model="type"
            class="mb-1"
          />

          <DictionaryEntrySelectorMultLevel
            v-if="displayUserRole"
            rules="required"
            :excludeEntries="['edb_main_role-upb']"
            v-model="role"
          />

          <ValidationProvider
            v-slot="{ errors }"
            :rules="passwordRules"
            name="Hasło"
          >
            <v-text-field
              title="Wpisz hasło"
              v-model="password"
              :append-icon="e1 ? 'mdi-eye-off' : 'mdi-eye'"
              @click:append="() => (e1 = !e1)"
              :type="e1 ? 'password' : 'text'"
              label="Hasło"
              :error-messages="errors"
              outlined
              persistent-placeholder
              placeholder="Wpisz hasło"
              autocomplete="new-password"
              name="password"
              :hint="passwordDesc"
              class="mb-1"
            />
          </ValidationProvider>

          <ValidationProvider
            name="Powtórz hasło"
            rules="required"
            v-slot="{ failed }"
          >
            <v-text-field
              title="Powtórz hasło"
              v-model="repeatPassword"
              :append-icon="e2 ? 'mdi-eye-off' : 'mdi-eye'"
              @click:append="() => (e2 = !e2)"
              :type="e2 ? 'password' : 'text'"
              label="Powtórz hasło"
              :error="failed"
              outlined
              persistent-placeholder
              placeholder="Wpisz hasło"
              autocomplete="new-password"
              name="repeatPassword"
              :hint="passwordDesc"
              class="mb-1"
            />
          </ValidationProvider>

          <ValidationProvider
            name="Recaptcha"
            rules="required"
            title="Zaznacz opcję, aby potwierdzić że nie jesteś robotem"
            v-slot="{ failed, errors }"
          >
            <vue-recaptcha
              right
              :class="{
                'mobile-recaptcha': $vuetify.breakpoint.smAndDown
              }"
              :sitekey="captcha.key"
              ref="recaptcha"
              @verify="onVerify"
              @expired="onExpired"
            ></vue-recaptcha>
            <LibErrorMessage :message="errors[0]" />
            <input type="hidden" :value="captcha.response" />
            <!-- <v-text-field
              hidden
              :value="captcha.response"
              :error="failed"
            ></v-text-field> -->
          </ValidationProvider>

          <div v-if="!admin">
            <div
              cols="12"
              class="pa-0"
              v-for="r in regulations"
              :key="r.regulationId"
            >
              <ValidationProvider :rules="r.rules" v-slot="{ failed }">
                <v-checkbox
                  v-model="regulation"
                  persistent-hint
                  :value="r.regulationId"
                  :error="failed"
                >
                  <template v-slot:label>
                    <div
                      class="ma-0"
                      @click.stop
                      v-if="r.shortContent.includes('href')"
                      v-html="r.shortContent"
                      title="Kliknij w link, aby otworzyć tą stronę"
                    />
                    <div
                      v-else
                      class="ma-0"
                      @click.stop
                      :title="tooltip.checkbox"
                      v-html="r.shortContent"
                    ></div>
                  </template>
                </v-checkbox>
              </ValidationProvider>
            </div>
          </div>
          <LoginAlerts
            :show="message.show"
            :type="message.color"
            :msg="message.msg"
            @close="message.show = false"
            dismissible
          />
          <div class="actions">
            <LibBtnSimple
              color="primary"
              underline
              rounded
              text="Zaloguj się"
              v-if="!admin"
              title="Kliknij, aby zalogować się do Elektronicznego Dziennika Budowy"
              :to="{ name: 'Login' }"
            />

            <v-btn
              rounded
              large
              color="primary"
              elevation="0"
              class="ml-2"
              :loading="createAccountDisabled"
              :disabled="createAccountDisabled"
              :class="{ 'black--text': $vuetify.theme.dark }"
              title="Kliknij, aby utworzyć nowe konto użytkownika Elektronicznego Dziennika Budowy"
              @click="submit"
            >
              Utwórz konto
            </v-btn>
          </div>
        </validation-observer>
      </v-form>
    </div>
  </div>
</template>

<script>
import VueRecaptcha from "vue-recaptcha";
import axios from "axios";
import DictionaryEntrySelectorMultLevel from "@/components/DictionaryEntrySelectorMultLevel";
import DictionaryEntrySelector from "@/components/DictionaryEntrySelector";
import LibBtnSimple from "@/components/library/common/LibBtnSimple.vue";
import LoginAlerts from "../components/LoginAlerts.vue";
import LibErrorMessage from "../components/library/common/LibErrorMessage.vue";

import {
  ValidationObserver,
  ValidationProvider,
  setInteractionMode
} from "vee-validate";
import { required, email, regex } from "vee-validate/dist/rules";
import { extend } from "vee-validate";

setInteractionMode("lazy");
extend("required", required);
extend("email", email);

extend("contains", {
  validate(value, args) {
    let val = args.value;
    return !!value.find((v) => v == val);
  },
  params: ["value"]
});
extend("password", {
  params: ["target"],
  validate(value, { target }) {
    return value === target;
  },
  message: "Hasła muszą być identyczne"
});

export default {
  name: "Register",
  components: {
    VueRecaptcha,
    DictionaryEntrySelectorMultLevel,
    DictionaryEntrySelector,
    ValidationProvider,
    ValidationObserver,
    LibBtnSimple,
    LoginAlerts,
    LibErrorMessage
  },
  props: {
    admin: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      repeatPassword: "",
      type: { dictionaryEntryCode: "usr_type-org" },
      role: [{ name: "edb_main_role", entry: null }],
      captcha: {
        key: process.env.VUE_APP_AUTH_RECAPTCHA_KEY,
        response: null
      },
      regulation: [],
      regulations: [],
      e1: true,
      e2: true,
      passwordRulesBase: {
        required: true,
        password: { target: "@Powtórz hasło" }
      },
      message: {
        show: false,
        color: "error",
        msg: "",
        timeout: 10000,
        closeAction: () => {}
      },
      createAccountDisabled: false
    };
  },

  computed: {
    passwordRules() {
      let param = this.$store.getters["conf/getConfigurationParam"](
        "password.strength"
      );
      if (param) {
        extend("regex", {
          ...regex,
          message: param.description
        });
        return Object.assign({}, this.passwordRulesBase, {
          regex: param.value
        });
      }
      return this.passwordRulesBase;
    },
    passwordDesc() {
      let param = this.$store.getters["conf/getConfigurationParam"](
        "password.strength"
      );
      return param?.description || "";
    },
    displayUserRole() {
      if (this.type && this.type.dictionaryEntryCode == "usr_type-org")
        return true;
      return false;
    },
    nameRules() {
      if (this.type && this.type.dictionaryEntryCode == "usr_type-ofiz")
        return "required";
      else return "";
    }
  },
  methods: {
    submit() {
      this.$refs.observer.validate().then((result) => {
        if (result === true) {
          let { dictionaryEntryCode: type } = this.type;
          let { firstName, lastName, email, password, regulation } = this;

          let role = this.role.map((r) => {
            if (r.entry) return r.entry.dictionaryEntryCode;
            return null;
          });

          role = role.filter((r) => r);

          if (!role || role.length == 0) role = ["edb_main_role-upb"];

          let user = {
            email,
            firstName,
            lastName,
            password,
            type,
            role,
            regulation,
            captcha: this.captcha.response
          };

          return axios
            .post(`${process.env.VUE_APP_AUTH_REST_URL}/registration`, user)
            .then(() => {
              let msg =
                "Na podany adres e-mail została wysłana wiadomość potwierdzająca założenie konta";
              window.location.replace(`/?msg=${msg}`);
              return true;
            })
            .catch((error) => {
              this.message.timeout = 10000;
              this.message.msg =
                error && error.response && error.response.data === "EXISTS_USER"
                  ? "Podany adres e-mail został już zarejestrowany w systemie EDB. Możesz się zalogować do systemu. Jeśli nie pamiętasz hasła, kliknij przycisk Nie pamiętasz hasła?"
                  : "Błąd. Proszę spróbować poźniej.";
              this.message.color = "error";
              this.message.show = true;
              this.message.closeAction = () => {
                this.message.show = false;
              };
              this.resetRecaptcha();
              this.resetSnack();
              this.$refs.observer.reset();
              return false;
            });
        }
      });
    },
    clear() {
      this.firstName = "";
      this.lastName = "";
      this.email = "";
      this.password = "";
      this.repeatPassword = "";
      this.type = null;
      this.role = [{ name: "edb_main_role", entry: null }];
      this.regulation = [];
    },
    onVerify: function(response) {
      this.captcha.response = response;
    },
    onExpired: function() {
      this.resetRecaptcha();
    },
    resetSnack() {
      setTimeout(() => {
        this.message.show = false;
      }, this.message.timeout + 1);
    },
    resetRecaptcha() {
      this.$refs.recaptcha.reset();
      this.captcha.response = null;
    },
    loadRegulation() {
      return axios
        .get(`${process.env.VUE_APP_AUTH_REST_URL}/regulation`)
        .then(({ data }) => {
          return data;
        });
    },
    stop() {}
  },
  created() {
    this.$store.dispatch("conf/load", ["password.strength"]);
    let recaptchaScript = document.createElement("script");
    recaptchaScript.setAttribute(
      "src",
      "https://www.google.com/recaptcha/api.js?onload=vueRecaptchaApiLoaded&render=explicit"
    );
    document.head.appendChild(recaptchaScript);

    this.loadRegulation().then((reg) => {
      this.regulations = reg.map((r) => {
        if (r.mustConfirm == true)
          r.rules = `required|contains:${r.regulationId}`;
        return r;
      });
    });

    if (this.$route.admin) {
      this.admin = this.$route.admin;
    }
  },
  metaInfo: {
    title: "Rejestracja"
  }
};
</script>

<style lang="scss">
.mobile-recaptcha {
  transform: scale(0.8);
  margin-left: -40px;
}
</style>
